
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import WebsiteRepositoryInterface, {
  Website,
  WebsiteCreation,
  WebsiteDeletion,
} from '@/services/Website/interfaces';
import PageHeader from '@/components/layout/PageHeader.vue';
import DeletionOrchestratorStatus from '@/components/website/DeletionOrchestratorStatus.vue';
import WebsiteErrorStatusHover from '@/components/website/WebsiteErrorStatusHover.vue';
import {
  ListPaginationOptions,
  buildListPaginationQuery,
} from '@/utils/listPagination';
import {
  OrchestratorRowState,
  fetchLatestOrchestrator,
} from '@/utils/websiteOrchestrator';

const ORCH_PAGE_SIZE_DEFAULT = 5;
const ORCH_FOOTER_PROPS = {
  'items-per-page-options': [5, 10, 25, 50, 100],
  showFirstLastPage: true,
};

@Component({
  inject: [
    'websiteRepository',
  ],
  components: {
    PageHeader,
    DeletionOrchestratorStatus,
    WebsiteErrorStatusHover,
  },
})
export default class Resources extends Vue {
  public loading = false;

  public websiteRepository!: WebsiteRepositoryInterface;

  public websiteSearch = '';

  public options: ListPaginationOptions = {
    page: 1,
    itemsPerPage: ORCH_PAGE_SIZE_DEFAULT,
  };

  public footerProps = ORCH_FOOTER_PROPS;

  public totalWebsitesCount = 0;

  public websites: Website[] = [];

  public orchestratorMap: { [websiteId: number]: OrchestratorRowState } = {};

  private orchestratorLoadToken = 0;

  private ignoreRouteSync = false;

  private applyingRouteQuery = false;

  public websiteStatuses: string[] = [
    'PENDING',
    'ACTIVE',
    'DISABLED',
    'ERROR',
  ];

  public headers: DataTableHeader[] = [
    { text: 'Id', value: 'id', width: '80px' },
    { text: 'Domain', value: 'domain' },
    { text: 'Status', value: 'status', width: '120px' },
    { text: 'Orchestrator', value: 'orchestrator', sortable: false, width: '320px' },
  ];

  public async created(): Promise<void> {
    this.applyRouteQuery();
    await this.loadWebsites();
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      const query = this.$route.query;
      if (typeof query.q === 'string') {
        this.websiteSearch = query.q;
      } else {
        this.websiteSearch = '';
      }

      const page = Number(query.page);
      this.options.page = page > 0 ? page : 1;

      const pageSize = Number(query.pageSize);
      this.options.itemsPerPage = pageSize > 0 ? pageSize : ORCH_PAGE_SIZE_DEFAULT;

      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public buildListQuery(): { [key: string]: string } {
    const extra: { [key: string]: string } = {};
    const search = (this.websiteSearch || '').trim();
    if (search) {
      extra.q = search;
    }

    const query = buildListPaginationQuery(this.options, extra);
    // buildListPaginationQuery omits pageSize when === 25; this page defaults to 5
    if (this.options.itemsPerPage === ORCH_PAGE_SIZE_DEFAULT) {
      delete query.pageSize;
    } else if (this.options.itemsPerPage > 0) {
      query.pageSize = String(this.options.itemsPerPage);
    }
    return query;
  }

  public listQueryEquals(
    routeQuery: { [key: string]: string | (string | null)[] | null | undefined },
  ): boolean {
    const next = this.buildListQuery();
    const keys = ['q', 'page', 'pageSize'];
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const left = typeof routeQuery[key] === 'string' ? routeQuery[key] : '';
      const right = next[key] || '';
      if (left !== right) {
        return false;
      }
    }
    return true;
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = this.buildListQuery();
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'resources', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public orchLoading(websiteId: number): boolean {
    const row = this.orchestratorMap[websiteId];
    return Boolean(row && row.loading);
  }

  public orchCreation(websiteId: number): WebsiteCreation | null {
    const row = this.orchestratorMap[websiteId];
    return row && row.creation ? row.creation : null;
  }

  public orchDeletion(websiteId: number): WebsiteDeletion | null {
    const row = this.orchestratorMap[websiteId];
    return row && row.deletion ? row.deletion : null;
  }

  public openSite(item: Website): void {
    this.$router.push({
      name: 'site_orchestrator',
      params: { websiteId: String(item.id) },
    });
  }

  public async runSearch(): Promise<void> {
    this.options.page = 1;
    this.loading = true;
    try {
      await this.syncRouteQuery();
      await this.loadWebsites();
    } catch (err) {
      this.$errorDialog.show('Failed to load websites.');
    } finally {
      this.loading = false;
    }
  }

  public async onPageChange(): Promise<void> {
    this.loading = true;
    try {
      await this.syncRouteQuery();
      await this.loadWebsites();
    } catch (err) {
      this.$errorDialog.show('Failed to load websites.');
    } finally {
      this.loading = false;
    }
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    this.loading = true;
    try {
      await this.syncRouteQuery();
      await this.loadWebsites();
    } catch (err) {
      this.$errorDialog.show('Failed to load websites.');
    } finally {
      this.loading = false;
    }
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'resources') {
      return;
    }
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.applyRouteQuery();
    this.loading = true;
    try {
      await this.loadWebsites();
    } catch (err) {
      this.$errorDialog.show('Failed to load websites.');
    } finally {
      this.loading = false;
    }
  }

  public async loadWebsites(): Promise<void> {
    this.loading = true;
    this.orchestratorLoadToken += 1;
    this.orchestratorMap = {};

    try {
      let domainSearch: string | undefined = (this.websiteSearch || '').trim() || undefined;
      let statusSearch: string | undefined;

      if (domainSearch && this.websiteStatuses.indexOf(domainSearch.toUpperCase()) !== -1) {
        statusSearch = domainSearch.toUpperCase();
        domainSearch = undefined;
      }

      const pageSize = this.options.itemsPerPage > 0
        ? this.options.itemsPerPage
        : ORCH_PAGE_SIZE_DEFAULT;

      const response = await this.websiteRepository.searchWebsites(
        this.options.page,
        domainSearch,
        undefined,
        undefined,
        statusSearch,
        pageSize,
      );

      this.websites = response.Websites || [];
      this.totalWebsitesCount = response.count || 0;
      this.loading = false;

      await this.loadOrchestratorForWebsites(this.websites);
    } catch (err) {
      this.websites = [];
      this.totalWebsitesCount = 0;
      this.loading = false;
      throw err;
    }
  }

  public async loadOrchestratorForWebsites(websites: Website[]): Promise<void> {
    const loadToken = this.orchestratorLoadToken + 1;
    this.orchestratorLoadToken = loadToken;

    if (!websites.length) {
      this.orchestratorMap = {};
      return;
    }

    const nextMap: { [websiteId: number]: OrchestratorRowState } = {};
    websites.forEach((website) => {
      nextMap[website.id] = { loading: true, creation: null, deletion: null };
    });
    this.orchestratorMap = { ...nextMap };

    await Promise.all(websites.map(async (website) => {
      if (this.orchestratorLoadToken !== loadToken) {
        return;
      }

      try {
        const resources = await fetchLatestOrchestrator(
          this.websiteRepository,
          website.id,
          false,
        );
        if (this.orchestratorLoadToken !== loadToken) {
          return;
        }
        this.$set(this.orchestratorMap, website.id, {
          loading: false,
          creation: resources.creation,
          deletion: resources.deletion,
        });
      } catch (err) {
        if (this.orchestratorLoadToken !== loadToken) {
          return;
        }
        this.$set(this.orchestratorMap, website.id, {
          loading: false,
          creation: null,
          deletion: null,
        });
      }
    }));
  }
}
